import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import { useQueryStates } from 'nuqs';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Icon } from '@clay-run/glaze/icon';
import { FLOW_ICONS, type FlowIconName } from '../../icons';
import { TeamLensItems } from '../../../TeamLensItems';
import { useUserControlsContext } from '../../../../contexts/UserControlsContext';
import { useRoleView, useSetViewAsRole } from '../../../../hooks/useRoleView';
import { useSetTerraDesignV2, useTerraDesignV2Enabled } from '../../../../hooks/useTerraDesignV2';
import { openImpersonationPalette } from '../../../../lib/impersonation-palette-store';
import { setTheme, useThemeSync } from '../../../../lib/theme';
import { modalParams } from '../../../../lib/urlParams';
import { openPanel } from '../panels/flow-panels-store';
import { useRailNavigate, type RailItem, type RailOverrides } from './rail-items';

const { done: Check, library: List } = FLOW_ICONS;

const ITEM_CLASS =
  'flex items-center gap-2.5 px-3 py-2 text-sm outline-none cursor-default transition-colors text-text-primary hover:bg-bg-secondary-hover focus:bg-bg-secondary-hover data-[active=true]:bg-bg-selected';

function ItemIcon({ name }: { name: FlowIconName }) {
  return (
    <span className="flex size-4 shrink-0 items-center justify-center">
      <Icon type={FLOW_ICONS[name]} />
    </span>
  );
}

/**
 * The trailing account group: what the avatar menu (`RailAccountMenu`) offers
 * on desktop, as Radix items in the same dropdown. Rendered only when the
 * manifest does not set `hideUserMenu`.
 */
function AccountGroup() {
  const navigate = useNavigate();
  const { isAdmin, canImpersonate } = useUserControlsContext();
  const { team, isLoaded } = useRoleView();
  const { setTeam } = useSetViewAsRole();
  const { signOut } = useClerk();
  const [, setModalParams] = useQueryStates(modalParams);
  const { enabled: designV2Enabled } = useTerraDesignV2Enabled();
  const { setEnabled: setDesignV2Enabled } = useSetTerraDesignV2();
  const theme = useThemeSync();

  return (
    <>
      {isLoaded && (
        <>
          <DropdownMenu.Separator className="h-px bg-border-primary" />
          <DropdownMenu.Group>
            <DropdownMenu.Label
              className="px-3 py-1.5 text-xs font-medium text-text-tertiary"
              data-testid="nav-rail-mobile-team-lens-label"
            >
              User Role
            </DropdownMenu.Label>
            <TeamLensItems team={team} onPick={setTeam} variant="inline" itemClassName={ITEM_CLASS} />
          </DropdownMenu.Group>
        </>
      )}
      <DropdownMenu.Separator className="h-px bg-border-primary" />
      <DropdownMenu.Group>
        {canImpersonate && (
          <DropdownMenu.Item className={ITEM_CLASS} onSelect={() => openImpersonationPalette()}>
            <ItemIcon name="switchUser" />
            Switch user…
          </DropdownMenu.Item>
        )}
        {(isAdmin || canImpersonate) && (
          <DropdownMenu.Item className={ITEM_CLASS} onSelect={() => navigate('/admin')}>
            <ItemIcon name="admin" />
            Admin
          </DropdownMenu.Item>
        )}
        <DropdownMenu.Item
          className={ITEM_CLASS}
          onSelect={() => {
            void setModalParams({ modal: 'preferences' });
          }}
        >
          <ItemIcon name="settings" />
          Preferences
        </DropdownMenu.Item>
      </DropdownMenu.Group>
      <DropdownMenu.Separator className="h-px bg-border-primary" />
      <DropdownMenu.Group>
        <DropdownMenu.CheckboxItem
          data-testid="nav-rail-mobile-design-v2"
          className={ITEM_CLASS}
          checked={designV2Enabled}
          onCheckedChange={(next) => setDesignV2Enabled(next)}
          onSelect={(e) => e.preventDefault()}
        >
          <ItemIcon name="designBeta" />
          Terra Beta
          <DropdownMenu.ItemIndicator className="ml-auto flex items-center justify-center">
            <Check className="size-4" />
          </DropdownMenu.ItemIndicator>
        </DropdownMenu.CheckboxItem>
        <DropdownMenu.CheckboxItem
          data-testid="nav-rail-mobile-theme-dark"
          className={ITEM_CLASS}
          checked={theme === 'dark'}
          onCheckedChange={(next) => setTheme(next ? 'dark' : 'light')}
          onSelect={(e) => e.preventDefault()}
        >
          <ItemIcon name="themeDark" />
          Dark theme
          <DropdownMenu.ItemIndicator className="ml-auto flex items-center justify-center">
            <Check className="size-4" />
          </DropdownMenu.ItemIndicator>
        </DropdownMenu.CheckboxItem>
      </DropdownMenu.Group>
      <DropdownMenu.Separator className="h-px bg-border-primary" />
      <DropdownMenu.Item className={ITEM_CLASS} onSelect={() => { void signOut(); }}>
        <ItemIcon name="logOut" />
        Log out
      </DropdownMenu.Item>
    </>
  );
}

/**
 * The rail below 768px: a floating hamburger (`fixed top-3 left-3 z-50`, zero
 * layout width) that opens one dropdown: the rail items in rail order (the
 * same `useRailItems` result the desktop strip renders), then Apps (unless
 * `hideTools`) and Chat, then the account group (unless `hideUserMenu`).
 * Ported from the old `NavigationRail` mobile branch.
 */
export default function MobileRailMenu({
  items,
  activeKey,
  overrides,
}: {
  items: readonly RailItem[];
  activeKey: string | null;
  overrides: RailOverrides;
}) {
  const { go, goItem } = useRailNavigate();

  return (
    <div className="fixed top-3 left-3 z-50">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            data-testid="nav-rail-mobile-trigger"
            aria-label="Open navigation menu"
            className="flex size-9 items-center justify-center rounded-lg border-[0.5px] border-border-primary bg-bg-primary text-text-primary shadow-md"
          >
            <List className="size-5" />
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            side="bottom"
            align="start"
            sideOffset={8}
            data-testid="nav-rail-mobile-dropdown"
            className="z-50 max-h-[80dvh] w-56 overflow-y-auto rounded-lg border-[0.5px] border-border-primary bg-bg-primary shadow-lg"
          >
            <DropdownMenu.Group>
              {items.map((item) => (
                <DropdownMenu.Item
                  key={item.key}
                  data-testid={`nav-rail-mobile-${item.key}`}
                  data-active={item.key === activeKey}
                  className={ITEM_CLASS}
                  onSelect={() => goItem(item)}
                >
                  <ItemIcon name={item.icon} />
                  {item.label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Group>
            <DropdownMenu.Separator className="h-px bg-border-primary" />
            <DropdownMenu.Group>
              {!overrides.hideTools && (
                <DropdownMenu.Item
                  data-testid="nav-rail-mobile-apps"
                  className={ITEM_CLASS}
                  onSelect={() => go('/tools')}
                >
                  <ItemIcon name="apps" />
                  Apps
                </DropdownMenu.Item>
              )}
              <DropdownMenu.Item
                data-testid="nav-rail-mobile-chat"
                className={ITEM_CLASS}
                onSelect={() => openPanel('chat')}
              >
                <ItemIcon name="chat" />
                Chat
              </DropdownMenu.Item>
            </DropdownMenu.Group>
            {!overrides.hideUserMenu && <AccountGroup />}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  );
}
